/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import { getTriAppConfig } from "../../TriAppConfig";

const WEB_CONTEXT_ID_HEADER_NAME = "triWebContextId";
const CONTENT_TYPE_HEADER_NAME = "Content-Type";
const COUNT_ONLY_PARAM_NAME = "countOnly";
const QUERY_PARAM_NAME = "query";
const ACTION_GROUP_PARAM_NAME = "actionGroup";
const ACTION_PARAM_NAME = "action";
const REFRESH_PARAM_NAME = "refresh";
const TYPE_PARAM_NAME = "type";
const METHOD_PARAM_NAME = "method";
const MULTI_CONTEXT_PARAM_NAME = "multiContextIds";
const INCLUDE_RESERVE_UNAVAILABLE_PARAM_NAME = "reserveIncludeUnavailable";
const NOOP_URL = "/p/webapi/noop";
const MODEL_API_URL = "/p/webapi/rest/v2";
const END_CONTEXT_URL = "/p/webapi/end";

const ModelRequestTypes = {
  GET: "GET",
  CREATE: "CREATE",
  DELETE: "DELETE",
  SAVE: "SAVE",
  ADD: "ADD",
  REMOVE: "REMOVE",
  GET_QUERY_METADATA: "GET_QUERY_METADATA",
};

let webContextId = null;
let currentWebContextRequest = null;

export function ModelError(responseStatus, responseError, error) {
  this.responseStatus = responseStatus;
  this.responseError = responseError;
  this.error = error;
}

async function createModelError(error, response) {
  if (response != null) {
    const contentType = response.headers.get("Content-Type");
    let responseError = null;
    if (contentType != null && contentType.indexOf("application/json") >= 0) {
      try {
        responseError = await response.json();
      } catch (error) {
        responseError = null;
      }
    }
    return new ModelError(response.status, responseError, error);
  } else if (error instanceof ModelError) {
    return error;
  } else {
    return new ModelError(-1, null, error);
  }
}

async function getWebContextId() {
  if (webContextId != null) return webContextId;
  if (currentWebContextRequest != null) return currentWebContextRequest;

  currentWebContextRequest = (async () => {
    const requestOptions = {
      method: "GET",
      cache: "no-cache",
      credentials: "include",
    };
    const noopRequest = new Request(
      `${getTriAppConfig().tririgaUrl}${NOOP_URL}`,
      requestOptions
    );
    const noopResponse = await fetch(noopRequest);
    if (!noopResponse.ok) {
      throw await createModelError(null, noopResponse);
    }
    return noopResponse.headers.get(WEB_CONTEXT_ID_HEADER_NAME);
  })();

  try {
    webContextId = await currentWebContextRequest;
    return webContextId;
  } catch (error) {
    webContextId = null;
    currentWebContextRequest = null;
    throw await createModelError(error);
  }
}

async function endWebContext() {
  const webContextIdHeader = await getWebContextId();
  if (webContextIdHeader == null) return;

  try {
    const requestOptions = {
      method: "GET",
      cache: "no-cache",
      headers: {},
      credentials: "include",
    };
    requestOptions.headers[WEB_CONTEXT_ID_HEADER_NAME] = webContextIdHeader;
    const endContextRequest = new Request(
      `${getTriAppConfig().tririgaUrl}${END_CONTEXT_URL}`,
      requestOptions
    );
    const endContextResponse = await fetch(endContextRequest);
    if (!endContextResponse.ok) {
      throw await createModelError(null, endContextResponse);
    }
  } catch (error) {
    webContextId = null;
    currentWebContextRequest = null;
    throw await createModelError(error);
  }
}

async function fetchModel(modelOptions) {
  try {
    const modelRequest = await buildModelRequest(modelOptions);
    const modelResponse = await fetch(modelRequest);
    if (!modelResponse.ok) {
      throw await createModelError(null, modelResponse);
    }
    return await modelResponse.json();
  } catch (error) {
    throw await createModelError(error);
  }
}

async function buildModelRequest(modelOptions) {
  const {
    modelAndViewName,
    instanceId,
    context,
    type,
    query,
    data,
    actionGroup,
    action,
    wfParametersMap,
    multiContextIds,
    refresh = false,
    countOnly = false,
    reserveIncludeUnavailable = false,
  } = modelOptions;
  const requestOptions = await buildBaseRequestOptions();
  requestOptions.method = computeMethod(type, query);
  requestOptions.body = computeBody(type, query, data, wfParametersMap);
  requestOptions.headers[CONTENT_TYPE_HEADER_NAME] = computeContentType(
    type,
    query
  );
  const modelUrl = computeModelUrl(
    modelAndViewName,
    instanceId,
    context,
    type,
    actionGroup,
    action,
    query,
    multiContextIds,
    refresh,
    countOnly,
    reserveIncludeUnavailable
  );
  return new Request(modelUrl, requestOptions);
}

function computeMethod(type, query) {
  let method;
  switch (type) {
    case ModelRequestTypes.GET:
      if (query != null) {
        method = "POST";
      } else {
        method = "GET";
      }
      break;
    case ModelRequestTypes.SAVE:
    case ModelRequestTypes.ADD:
    case ModelRequestTypes.REMOVE:
      method = "PUT";
      break;
    case ModelRequestTypes.CREATE:
    case ModelRequestTypes.DELETE:
      method = "POST";
      break;
    case ModelRequestTypes.GET_QUERY_METADATA:
      method = "GET";
      break;
  }
  return method;
}

function computeBody(type, query, data, wfParametersMap) {
  let body = null;

  switch (type) {
    case ModelRequestTypes.GET: {
      if (query != null) {
        body = JSON.stringify(query);
      }
      break;
    }
    case ModelRequestTypes.CREATE:
    case ModelRequestTypes.DELETE:
    case ModelRequestTypes.SAVE:
    case ModelRequestTypes.ADD:
    case ModelRequestTypes.REMOVE: {
      const bodyObject = {
        data,
      };
      if (query != null) {
        bodyObject.query = query;
      }
      if (wfParametersMap != null) {
        bodyObject.wfParametersMap = wfParametersMap;
      }
      body = JSON.stringify(bodyObject);
      break;
    }
  }

  return body;
}

function computeContentType(type, query) {
  let contentType = "";
  switch (type) {
    case ModelRequestTypes.GET:
      if (query != null) {
        contentType = "application/json";
      }
      break;
    case ModelRequestTypes.CREATE:
    case ModelRequestTypes.SAVE:
    case ModelRequestTypes.ADD:
    case ModelRequestTypes.REMOVE:
    case ModelRequestTypes.DELETE:
      contentType = "application/json";
      break;
  }
  return contentType;
}

async function buildBaseRequestOptions() {
  const webContextIdHeader = await getWebContextId();
  const requestOptions = {
    cache: "no-cache",
    headers: {},
    credentials: "include",
  };
  requestOptions.headers[WEB_CONTEXT_ID_HEADER_NAME] = webContextIdHeader;
  return requestOptions;
}

function computeModelUrl(
  modelAndViewName,
  instanceId,
  context,
  type,
  actionGroup,
  action,
  query,
  multiContextIds,
  refresh,
  countOnly,
  reserveIncludeUnavailable
) {
  const modelUrl = new URL(
    `${
      getTriAppConfig().tririgaUrl
    }${MODEL_API_URL}/${modelAndViewName}/${instanceId}/${context}`
  );

  switch (type) {
    case ModelRequestTypes.CREATE:
    case ModelRequestTypes.SAVE:
    case ModelRequestTypes.ADD:
    case ModelRequestTypes.REMOVE:
    case ModelRequestTypes.DELETE:
      if (actionGroup != null) {
        modelUrl.searchParams.set(ACTION_GROUP_PARAM_NAME, actionGroup);
      }
      if (action != null) {
        modelUrl.searchParams.set(ACTION_PARAM_NAME, action);
      }
      if (refresh) {
        modelUrl.searchParams.set(REFRESH_PARAM_NAME, "true");
      }
      break;
  }

  switch (type) {
    case ModelRequestTypes.GET:
      if (query != null) {
        modelUrl.searchParams.set(QUERY_PARAM_NAME, "true");
      }
      if (countOnly) {
        modelUrl.searchParams.set(COUNT_ONLY_PARAM_NAME, "true");
      }
      if (reserveIncludeUnavailable) {
        modelUrl.searchParams.set(
          INCLUDE_RESERVE_UNAVAILABLE_PARAM_NAME,
          "true"
        );
      }
      if (multiContextIds != null && multiContextIds.length > 0) {
        modelUrl.searchParams.set(
          MULTI_CONTEXT_PARAM_NAME,
          multiContextIds.join()
        );
      }
      break;
    case ModelRequestTypes.ADD:
      modelUrl.searchParams.set(TYPE_PARAM_NAME, "add");
      break;
    case ModelRequestTypes.REMOVE:
      modelUrl.searchParams.set(TYPE_PARAM_NAME, "remove");
      break;
    case ModelRequestTypes.DELETE:
      modelUrl.searchParams.set(METHOD_PARAM_NAME, "delete");
      break;
    case ModelRequestTypes.GET_QUERY_METADATA:
      modelUrl.searchParams.set(METHOD_PARAM_NAME, "dsMetadata");
      break;
  }
  return modelUrl;
}

export default {
  getWebContextId,
  fetchModel,
  ModelRequestTypes,
  endWebContext,
  ModelError,
};
